import { Component, EventEmitter, Input, Output, ViewChild, ChangeDetectionStrategy } from '@angular/core'
import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay'
import { MatChipsModule } from '@angular/material/chips'
import { MatIcon } from '@angular/material/icon'
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core'
import { MatSelect, MatSelectModule } from '@angular/material/select'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { MatFormFieldModule } from '@angular/material/form-field'
import { MatInput } from '@angular/material/input'
import { MatListOption, MatSelectionList } from '@angular/material/list'

@Component({
  selector: 'app-multi-filter',
  standalone: true,
  imports: [
    CdkConnectedOverlay,
    CdkOverlayOrigin,
    MatChipsModule,
    MatIcon,
    TranslatePipe,
    TranslateDirective,
    MatSelectModule,
    MatFormFieldModule,
    FormsModule,
    ReactiveFormsModule,
    MatInput,
    MatSelectionList,
    MatListOption,
  ],
  templateUrl: './multi-filter.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './multi-filter.component.scss',
})
export class MultiFilterComponent<T, K extends keyof T> {
  @Input()
  name!: string

  @Input()
  idKey!: K

  @Input()
  labelTranslationKey!: string

  @Input()
  icon!: string

  @Input()
  fontSet: string | null = null

  @Input()
  presets: { label: string; ids: NonNullable<T[K]>[] }[] = []

  _value: NonNullable<T[K]>[] = []

  @Input()
  set values(value: NonNullable<T[K]>[]) {
    this._value = JSON.parse(JSON.stringify(value))
  }

  get values(): NonNullable<T[K]>[] {
    return this._value
  }

  @Input()
  options!: T[]

  @Input()
  displayWith!: (value: T) => string

  @Input()
  displaySelected!: (values: NonNullable<T[K]>[]) => string

  @Output()
  valueChange = new EventEmitter<NonNullable<T[K]>[]>()

  @Output()
  clear = new EventEmitter<void>()

  @ViewChild(MatSelect)
  selectComponent!: MatSelect

  isOpen = false
  query = ''

  get filteredOptions(): T[] {
    return this.options.filter((option) => JSON.stringify(option).toLowerCase().includes(this.query.toLowerCase()))
  }

  sendClear(): void {
    this.clear.emit()
  }

  applyPreset(preset: { label: string; ids: NonNullable<T[K]>[] }) {
    this._value = [...preset.ids]
    this.valueChange.emit(this._value)
  }

  isOptionSelected(option: T) {
    return this.values.includes(option[this.idKey]!)
  }

  changeSelectionStatus(option: T, isSelected: boolean) {
    const optionValue = option[this.idKey]!

    // Don't allow deselecting the values hidden due to the query
    const isHiddenValue = this.filteredOptions.map((filteredOption) => filteredOption[this.idKey]).includes(optionValue)
    if (!isHiddenValue) {
      return
    }

    if (isSelected) {
      this.values.push(optionValue)
    } else {
      this.values = this.values.filter((value) => value !== optionValue)
    }

    // Remove the duplicates that occur due to the handler being called also when it initializes after the query has been changed
    this.values = [...new Set(this.values)]

    this.valueChange.emit(this.values)
  }
}
